export const DRAFT_TEAMS = 12;
export const DRAFT_ROUNDS = 18;
export const DRAFT_SLOT_COUNT = DRAFT_TEAMS * DRAFT_ROUNDS;
export const UNDRAFTED_COUNT = 108;
export const TOTAL_SLOT_COUNT = DRAFT_SLOT_COUNT + UNDRAFTED_COUNT;

const STORAGE_KEY_PREFIX = 'eliminator-draft-board';

export function draftBoardStorageKey(season) {
	return `${STORAGE_KEY_PREFIX}:${season}`;
}

export function overallPickToCoords(overallPick, teams = DRAFT_TEAMS) {
	const round = Math.floor(overallPick / teams);
	const posInRound = overallPick % teams;
	const teamIndex = round % 2 === 0 ? posInRound : teams - 1 - posInRound;
	return { round, teamIndex };
}

export function coordsToOverallPick(teamIndex, round, teams = DRAFT_TEAMS) {
	const posInRound = round % 2 === 0 ? teamIndex : teams - 1 - teamIndex;
	return round * teams + posInRound;
}

export function getTeamSlotEntryIds(slots, teamIndex, rounds = DRAFT_ROUNDS, teams = DRAFT_TEAMS) {
	const ids = [];
	for (let round = 0; round < rounds; round += 1) {
		const entryId = slots[coordsToOverallPick(teamIndex, round, teams)];
		if (entryId != null) ids.push(entryId);
	}
	return ids;
}

export function isRoundLeftToRight(round) {
	return round % 2 === 0;
}

export function isUndraftedSlot(index) {
	return index >= DRAFT_SLOT_COUNT;
}

export function emptyDraftSlots(slotCount = TOTAL_SLOT_COUNT) {
	return Array.from({ length: slotCount }, () => null);
}

function sortedEntryIds(entries) {
	return [...entries]
		.sort((a, b) => (a.rank || 0) - (b.rank || 0))
		.map((entry) => entry.id);
}

export function seedDraftSlotsFromEntries(entries, slotCount = TOTAL_SLOT_COUNT) {
	const slots = emptyDraftSlots(slotCount);
	sortedEntryIds(entries)
		.slice(0, slotCount)
		.forEach((id, index) => {
			slots[index] = id;
		});
	return slots;
}

export function ensureDraftSlotsWithUndrafted(slots, entries) {
	const sortedIds = sortedEntryIds(entries);
	const next = Array.isArray(slots) ? [...slots] : emptyDraftSlots(DRAFT_SLOT_COUNT);
	const used = new Set(next.filter((id) => id != null));
	const remaining = sortedIds.filter((id) => !used.has(id));

	if (next.length > TOTAL_SLOT_COUNT) {
		return next.slice(0, TOTAL_SLOT_COUNT);
	}

	while (next.length < TOTAL_SLOT_COUNT) {
		const id = remaining.shift();
		if (id != null) used.add(id);
		next.push(id != null ? id : null);
	}

	return next;
}

export function swapDraftSlots(slots, fromIndex, toIndex) {
	if (
		fromIndex === toIndex ||
		fromIndex < 0 ||
		toIndex < 0 ||
		fromIndex >= slots.length ||
		toIndex >= slots.length
	) {
		return slots;
	}

	const next = [...slots];
	const temp = next[fromIndex];
	next[fromIndex] = next[toIndex];
	next[toIndex] = temp;
	return next;
}

export function loadDraftSlots(season) {
	if (typeof window === 'undefined') return null;

	try {
		const raw = window.localStorage.getItem(draftBoardStorageKey(season));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		if (
			parsed.length !== DRAFT_SLOT_COUNT &&
			parsed.length !== TOTAL_SLOT_COUNT
		) {
			return null;
		}
		return parsed.map((id) => {
			if (id == null) return null;
			const num = Number(id);
			return Number.isFinite(num) ? num : null;
		});
	} catch (error) {
		return null;
	}
}

export function saveDraftSlots(season, slots) {
	if (typeof window === 'undefined') return;

	try {
		window.localStorage.setItem(draftBoardStorageKey(season), JSON.stringify(slots));
	} catch (error) {
		// Ignore quota / private mode failures.
	}
}

export function formatDraftPickLabel(overallPick, teams = DRAFT_TEAMS) {
	if (isUndraftedSlot(overallPick)) {
		return 'UD';
	}
	const round = Math.floor(overallPick / teams) + 1;
	const pickInRound = (overallPick % teams) + 1;
	return `${round}.${String(pickInRound).padStart(2, '0')}`;
}

export function formatShortPlayerName(entry) {
	if (!entry) return '';
	const first = (entry.first_name || '').trim();
	const last = (entry.last_name || '').trim();
	if (first && last) return `${first.charAt(0)}. ${last}`;
	return entry.full_name || last || first || 'Player';
}

export function getTeamAbbreviation(entry, teamLookup) {
	if (!entry) return '';
	const key = (entry.team_name || '').toString().trim().toLowerCase();
	const team = teamLookup?.[key];
	if (team?.abbreviation) return team.abbreviation;
	return '';
}
